import { isBefore } from 'date-fns';

import {
  APPOINTMENT_TASKS_STATUS,
  APPOINTMENT_TASK_RENDER_TYPES,
  APPOINTMENT_TASK_TYPES,
} from 'constants/AppointmentConstants';
import { BOOKING_STATUSES, LOCKER_STATUS, MODES } from 'constants/LockerConstants';
import * as LockerUtils from 'modules/lockers/utils/appointmentTask';

/*
 * Calculate the booking status
 * - This function is a copy of the backed function
 */
export const getBookingStatus = ({ status, task } = {}) => {
  const { from } = task ?? {};
  const isPrepared = status === APPOINTMENT_TASKS_STATUS.PREPARED;
  return isPrepared && isBefore(from, new Date().toISOString()) ? BOOKING_STATUSES.USED : BOOKING_STATUSES.FREE;
};

export const getMode = (appointmentTask, locker) => {
  if (appointmentTask) {
    const { status } = appointmentTask ?? {};
    const { status: lockerStatus, bookingStatus = getBookingStatus(appointmentTask) } = locker ?? {};

    if (lockerStatus === LOCKER_STATUS.INACTIVE) {
      return MODES.NONE;
    }

    if (LockerUtils.isPreparationStatus(status, bookingStatus)) {
      return MODES.PREPARATION;
    }

    if (LockerUtils.isUnpreparationStatus(status, bookingStatus)) {
      return MODES.UNPREPARATION;
    }

    if (LockerUtils.isVerificationStatus(bookingStatus)) {
      return MODES.VERIFICATION;
    }

    return MODES.NONE;
  }
  return null;
};

export const canUnprepare = (appointmentTaskStatus, bookingStatus) =>
  appointmentTaskStatus === APPOINTMENT_TASKS_STATUS.PREPARED && bookingStatus === BOOKING_STATUSES.FREE;

export const isPreparationMode = mode => mode === MODES.PREPARATION;

export const isVerificationMode = mode => mode === MODES.VERIFICATION;

export const isUnpreparationMode = mode => mode === MODES.UNPREPARATION;

export const isDropOffTask = task => task?.type === APPOINTMENT_TASK_TYPES.KEY_DEPOSIT;

export const isPickupTask = task => task?.type === APPOINTMENT_TASK_TYPES.KEY_RETRIEVE;

export const canSetRentalKey = (task, mode) => {
  const isDropOff = isDropOffTask(task);
  const isPickup = isPickupTask(task);
  const isVerification = isVerificationMode(mode);

  return (isDropOff || isPickup) && isVerification;
};

export const canShowRental = (task, mode) => {
  const isDropOff = isDropOffTask(task);
  const isPickup = isPickupTask(task);
  const isVerification = isVerificationMode(mode);

  return isDropOff || (isPickup && isVerification);
};

export const canSetCustomerKey = (appointmentTask, locker) => {
  const mode = getMode(appointmentTask, locker);
  const { task } = appointmentTask;
  const isPreparation = isPreparationMode(mode);
  const isVerification = isVerificationMode(mode);
  const isDropOff = isDropOffTask(task);
  const isPickup = isPickupTask(task);

  if (isDropOff && isVerification) {
    return true;
  }
  if (isPickup && (isPreparation || isVerification)) {
    return true;
  }
  return false;
};

export const mustPutKeyInLocker = (task, mode, hasRental) => type => {
  const isPreparation = isPreparationMode(mode);
  const isDropOff = isDropOffTask(task);
  const isPickup = isPickupTask(task);

  if (isPreparation) {
    if (type === APPOINTMENT_TASK_RENDER_TYPES.RENTAL && isDropOff && hasRental) {
      return true;
    }

    if (type === APPOINTMENT_TASK_RENDER_TYPES.CLIENT && isPickup) {
      return true;
    }
  }
  return false;
};

export const mustControlLocker = (task, mode, hasRental) => type => {
  const isVerification = isVerificationMode(mode);
  const isDropOff = isDropOffTask(task);
  const isPickup = isPickupTask(task);

  if (isVerification) {
    if (isPickup && type === APPOINTMENT_TASK_RENDER_TYPES.CLIENT) {
      return true;
    }

    if (isDropOff && hasRental && type === APPOINTMENT_TASK_RENDER_TYPES.RENTAL) {
      return true;
    }
  }

  return false;
};

export const mustGetKeyFromLocker = (task, mode, hasRental) => type => {
  const isVerification = isVerificationMode(mode);
  const isDropOff = isDropOffTask(task);
  const isPickup = isPickupTask(task);

  if (isVerification) {
    if (type === APPOINTMENT_TASK_RENDER_TYPES.CLIENT && isDropOff) {
      return true;
    }

    if (type === APPOINTMENT_TASK_RENDER_TYPES.RENTAL && isPickup && hasRental) {
      return true;
    }
  }
  return false;
};

export const getTaskDescription = (task, mode, hasRental) => type => {
  const isPreparation = isPreparationMode(mode);
  const isVerification = isVerificationMode(mode);
  const isDropOff = isDropOffTask(task);
  const isPickup = isPickupTask(task);
  const isClientView = type === APPOINTMENT_TASK_RENDER_TYPES.CLIENT;
  const isRentalView = type === APPOINTMENT_TASK_RENDER_TYPES.RENTAL;

  if (isDropOff) {
    if (isPreparation && !isClientView && hasRental) {
      return {
        id: 'page.keyDeposit.lockers.details.checks.insert.rentalKeys',
        defaultMessage: 'Put rental car keys in the safe',
      };
    }

    if (isVerification) {
      if (isClientView) {
        return {
          id: 'page.keyDeposit.lockers.details.checks.get.clientKeys',
          defaultMessage: 'Get the client car keys in the safe',
        };
      }

      if (hasRental) {
        return {
          id: 'page.keyDeposit.lockers.details.checks.check.noRentalKeys',
          defaultMessage: 'Check that the rental car key are no present in the safe',
        };
      }
    }
  }

  if (isPickup) {
    if (isClientView) {
      if (isPreparation) {
        return {
          id: 'page.keyDeposit.lockers.details.checks.insert.clientKeys',
          defaultMessage: 'Put the client car keys in the safe',
        };
      }
      if (isVerification) {
        return {
          id: 'page.keyDeposit.lockers.details.checks.check.noClientKeys',
          defaultMessage: 'Check that the client car key are no present in the safe',
        };
      }
    }

    if (isRentalView && hasRental && isVerification) {
      return {
        id: 'page.keyDeposit.lockers.details.checks.get.rentalKeys',
        defaultMessage: 'Get the rental car keys in the safe',
      };
    }
  }
  return null;
};
